import * as React from 'react';
import { useEffect } from 'react';
import {
  Routes,
  Route,
} from 'react-router-dom';
import ThemeConfig from './theme/ThemeConfig';
import Layout from './components/layout/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserSettingsProvider, useUserSettings } from './context/UserSettingsContext';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import ProtectedRoute from './components/layout/ProtectedRoute';
import RegistrationValidationDialog from './components/auth/RegistrationValidationDialog';
import ProductSelectionDialog from './components/auth/ProductSelectionDialog';
import SessionTimeoutWrapper from './components/common/SessionTimeoutWrapper';
import PostAuthRedirect from './components/layout/PostAuthRedirect';
import LazyWrapper from './components/common/LazyWrapper';

// Lazy load all sensitive application components - these will only be downloaded when accessed
// Using webpack magic comments for better chunk naming and organization
const SpecialFloodedEvaporatorUI = React.lazy(() => import(/* webpackChunkName: "special-flooded-evap" */ './components/special_flooded_evaporator/SpecialFloodedEvaporatorUI'));
const SpecialCondenserUI = React.lazy(() => import(/* webpackChunkName: "special-condenser" */ './components/special_condenser/SpecialCondenserUI'));
const CoaxialEvaporatorUI = React.lazy(() => import(/* webpackChunkName: "coaxial-evaporator" */ './components/coaxial_evaporator/CoaxialEvaporatorUI'));
const CoaxialCondenserUI = React.lazy(() => import(/* webpackChunkName: "coaxial-condenser" */ './components/coaxial_condenser/CoaxialCondenserUI'));
const FloodedEvaporatorUI = React.lazy(() => import(/* webpackChunkName: "flooded-evaporator" */ './components/flooded_evaporator/FloodedEvaporatorUI'));
const DryExpansionEvaporatorUI = React.lazy(() => import(/* webpackChunkName: "dry-expansion-evap" */ './components/dry_expansion_evaporator/DryExpansionEvaporatorUI'));
const CondenserUI = React.lazy(() => import(/* webpackChunkName: "condenser" */ './components/condenser/CondenserUI'));
const ColdPlateUI = React.lazy(() => import(/* webpackChunkName: "cold-plate" */ './components/cold_plate/ColdPlateUI'));
const SpecialFloodedEvaporatorAIUI = React.lazy(() => import(/* webpackChunkName: "special-flooded-evap-ai" */ './components/special_flooded_evaporator_ai/SpecialFloodedEvaporatorAIUI'));
const TubeComparisonUI = React.lazy(() => import(/* webpackChunkName: "tube-comparison" */ './components/tube_comparison'));
const ExpertApp = React.lazy(() => import(/* webpackChunkName: "expert-app" */ './components/heat_transfer_expert/HeatTranferExpertUI'));
const DocumentsPage = React.lazy(() => import(/* webpackChunkName: "documents" */ './pages/DocumentsPage'));
const SharePage = React.lazy(() => import(/* webpackChunkName: "share" */ './pages/SharePage'));
const AirHeatExchangerInternalUI = React.lazy(() => import(/* webpackChunkName: "air-heat-exchanger" */ './components/air_heatexchanger_internal/AirHeatExchangerInternalUI'));

// About page component
const TermsOfUse: React.FC = () => (
  <Layout title="Terms of Use">
    <div>
      <p>
        Please review our Terms of Use for the ThermalX platform.
      </p>
      <a 
        href="https://prodts01st.blob.core.windows.net/public/thermalx_terms_of_use.pdf" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        View Terms of Use
      </a>
    </div>
  </Layout>
);

// Help page component
const PrivacyPolicy: React.FC = () => (
  <Layout title="Privacy Policy">
    <div>
      <p>
        Please review our Privacy Policy to understand how we handle your data.
      </p>
      <a 
        href="https://www.wieland.com/en/privacy-policy" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        View Privacy Policy
      </a>
    </div>
  </Layout>
);

// Special Flooded Evaporator UI page
const SpecialFloodedEvaporatorUIPage: React.FC = () => (
  <Layout title="Special Flooded Evaporator Calculator">
    <SpecialFloodedEvaporatorUI />
  </Layout>
);

// Special Condenser UI page
const SpecialCondenserUIPage: React.FC = () => (
  <Layout title="Special Condenser Calculator">
    <SpecialCondenserUI />
  </Layout>
);

// Coaxial Evaporator UI page
const CoaxialEvaporatorUIPage: React.FC = () => (
  <Layout title="Coaxial Evaporator Calculator">
    <CoaxialEvaporatorUI />
  </Layout>
);

// Coaxial Condenser UI page
const CoaxialCondenserUIPage: React.FC = () => (
  <Layout title="Coaxial Condenser Calculator">
    <CoaxialCondenserUI />
  </Layout>
);

// Documents page component
const DocumentsUIPage: React.FC = () => (
  <Layout title="Documents">
    <DocumentsPage />
  </Layout>
);

// Cold Plate UI page
const ColdPlateUIPage: React.FC = () => (
  <Layout title="Cold Plate Calculator">
    <ColdPlateUI />
  </Layout>
);

// Flooded Evaporator UI page
const FloodedEvaporatorUIPage: React.FC = () => (
  <Layout title="Flooded Evaporator Calculator">
    <FloodedEvaporatorUI />
  </Layout>
);

// Special Flooded Evaporator AI UI page
const SpecialFloodedEvaporatorAIUIPage: React.FC = () => (
  <Layout title="Special Flooded Evaporator AI Calculator">
    <SpecialFloodedEvaporatorAIUI />
  </Layout>
);

// Expert App page
const ExpertAppPage: React.FC = () => (
  <Layout title="Heat Transfer Expert App">
    <ExpertApp />
  </Layout>
);

// Air Heat Exchanger UI page
const AirHeatExchangerUIPage: React.FC = () => (
  <Layout title="Air Heat Exchanger">
    <AirHeatExchangerInternalUI />
  </Layout>
);

// Tube Comparison UI page
const TubeComparisonUIPage: React.FC = () => (
  <Layout title="Tube Comparison Calculator">
    <TubeComparisonUI />
  </Layout>
);

// Dry Expansion Evaporator UI page
const DryExpansionEvaporatorUIPage: React.FC = () => (
  <Layout title="Dry Expansion Evaporator Calculator">
    <DryExpansionEvaporatorUI />
  </Layout>
);

// Condenser UI page
const CondenserUIPage: React.FC = () => (
  <Layout title="Condenser Calculator">
    <CondenserUI />
  </Layout>
);

// New component to handle permission checks and dialogs
interface PermissionCheckProps {
  children?: React.ReactNode;
}

const PermissionCheck: React.FC<PermissionCheckProps> = ({ children }) => {
  const { isAuthenticated, appAccess } = useAuth();
  const { settings, loading } = useUserSettings();

  // If user is authenticated but has no app access
  if (isAuthenticated && (!appAccess || appAccess.accessible_apps.length === 0)) {
    // Don't show anything while settings are loading
    if (loading) {
      return null;
    }
    
    // Simple logic: show product selection ONLY if registration mail hasn't been sent yet
    if (settings.registration_mail_sent !== true) {
      return (
        <ProductSelectionDialog
          open={true}
          onComplete={() => {
            // Dialog completed - the ProductSelectionDialog handles updating the backend
            // and sending the registration mail
          }}
        />
      );
    }

    // Show registration validation if registration mail has been sent
    return <RegistrationValidationDialog />;
  }

  // If children are provided, render them, otherwise render LandingPage
  return <>{children || <LandingPage />}</>;
};

// Root component that handles authentication and permissions
const Root: React.FC = () => {
  return <PermissionCheck />;
};

// Protected component that checks for permissions
const ProtectedComponent: React.FC<{ component: React.ReactNode }> = ({ component }) => {
  return <PermissionCheck>{component}</PermissionCheck>;
};

const AppContent: React.FC = () => {
  const { checkAuthStatus } = useAuth();

  useEffect(() => {
    const handleUnauthorized = () => {
      checkAuthStatus();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [checkAuthStatus]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Root />
          </ProtectedRoute>
        }
      />
      <Route
        path="/terms-of-use"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={<TermsOfUse />} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={<PrivacyPolicy />} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Documents..." appName="Documents" appId="documents">
                <DocumentsUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/special-flooded-evaporator"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Special Flooded Evaporator..." appName="Special Flooded Evaporator" appId="special_flooded_evaporator">
                <SpecialFloodedEvaporatorUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/special-condenser"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Special Condenser..." appName="Special Condenser" appId="special_condenser">
                <SpecialCondenserUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaxial-evaporator"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Coaxial Evaporator..." appName="Coaxial Evaporator" appId="coaxial_evaporator">
                <CoaxialEvaporatorUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaxial-condenser"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Coaxial Condenser..." appName="Coaxial Condenser" appId="coaxial_condenser">
                <CoaxialCondenserUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-plate"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Cold Plate Calculator..." appName="Cold Plate" appId="cold_plate">
                <ColdPlateUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flooded-evaporator"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Flooded Evaporator..." appName="Flooded Evaporator" appId="flooded_evaporator">
                <FloodedEvaporatorUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/special-flooded-evaporator-ai"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading AI Calculator..." appName="Special Flooded Evaporator AI" appId="special_flooded_evaporator_ai">
                <SpecialFloodedEvaporatorAIUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/air-heat-exchanger"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Air Heat Exchanger..." appName="Air Heat Exchanger" appId="air_heatexchanger">
                <AirHeatExchangerUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tube-comparison"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Tube Comparison..." appName="Tube Comparison" appId="tube_comparison">
                <TubeComparisonUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert-app"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Heat Transfer Expert App..." appName="Heat Transfer Expert App" appId="expert_app">
                <ExpertAppPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dry-expansion-evaporator"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Dry Expansion Evaporator..." appName="Dry Expansion Evaporator" appId="dry_expansion_evaporator">
                <DryExpansionEvaporatorUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/condenser"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading Condenser..." appName="Condenser" appId="condenser">
                <CondenserUIPage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
      <Route
        path="/share"
        element={
          <ProtectedRoute>
            <ProtectedComponent component={
              <LazyWrapper fallbackMessage="Loading shared data..." appName="Share" appId="share">
                <SharePage />
              </LazyWrapper>
            } />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// App component wraps AppContent with AuthProvider and UserSettingsProvider, then ThemeConfig.
const App: React.FC = () => {
  return (
    <AuthProvider>
      <UserSettingsProvider>
        <ThemeConfig>
          <AppContent />
          <SessionTimeoutWrapper />
          <PostAuthRedirect />
        </ThemeConfig>
      </UserSettingsProvider>
    </AuthProvider>
  );
};

export default App;
